import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../context/LanguageContext';

/** Remove trailing brand segments so we never render "… | EWANDS | EWANDS". */
function stripTrailingBrandSegments(rawTitle, siteName) {
  if (!rawTitle || typeof rawTitle !== 'string') return '';
  let t = rawTitle.trim();
  const escaped = siteName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const trailingBrand = new RegExp(`\\s*\\|\\s*${escaped}(?:\\s+AI\\s+News)?\\s*$`, 'i');
  for (let i = 0; i < 5; i++) {
    const next = t.replace(trailingBrand, '').trim();
    if (next === t) break;
    t = next;
  }
  return t;
}

/**
 * SEO Component for dynamic meta tags, Open Graph, and structured data
 * @param {string} title - Page title (will be appended with site name)
 * @param {string} description - Page meta description
 * @param {string} keywords - Page-specific keywords (comma-separated)
 * @param {string} canonicalPath - Canonical path (without domain)
 * @param {string} ogType - Open Graph type (default: website)
 * @param {string} ogImage - Open Graph image URL
 * @param {object} structuredData - JSON-LD structured data object
 */
const SEO = ({ 
  title, 
  description, 
  keywords = '',
  canonicalPath = '',
  ogType = 'website',
  ogImage = '/Images/logo.png',
  structuredData = null
}) => {
  const { language } = useLanguage();
  const siteUrl = 'https://www.ewands.com';
  const siteName = 'EWANDS';
  
  // Core keywords for all pages
  const coreKeywords = 'AI company Malaysia, top AI software company Malaysia, custom software development AI, AI solutions Malaysia, AI implementation and consultation, predictive AI big data, AI agents enterprise, AI servers Malaysia, enterprise AI solutions, software development Malaysia, AI governance Malaysia, Malaysia AI technology';
  const fullKeywords = keywords ? `${keywords}, ${coreKeywords}` : coreKeywords;

  const pathNoLang = (canonicalPath || '').replace(/^\/(en|bm|zh)(\/|$)/, '/').replace(/^\/$/, '');
  const isHomePage = pathNoLang === '';

  /** Short org line — avoid repeating the homepage meta verbatim (helps sitelink snippets stay page-specific). */
  const organizationDescription =
    'EWANDS SDN BHD — Malaysian enterprise AI platform vendor, integrations, governed deployment, and custom software for ASEAN.';
  
  // Full title with site name (dedupe trailing "| EWANDS" from callers)
  const baseTitle = stripTrailingBrandSegments(title, siteName);
  const fullTitle = baseTitle ? `${baseTitle} | ${siteName}` : `${siteName} - Malaysia Top AI Software Company | Enterprise AI Solutions`;
  
  // Canonical URL
  const canonicalUrl = `${siteUrl}${canonicalPath}`;
  
  // Alternate language URLs
  const alternateUrls = ['en', 'bm', 'zh'].map(lang => ({
    lang,
    url: `${siteUrl}/${lang}${canonicalPath.replace(/^\/(en|bm|zh)/, '')}`
  }));

  // Default structured data for organization
  const defaultStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "EWANDS SDN BHD",
    "url": siteUrl,
    "logo": `${siteUrl}/Images/logo.png`,
    "description": organizationDescription,
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "13-6, Block C1, Jalan PJU 1/41, Dataran Prima",
      "addressLocality": "Petaling Jaya",
      "addressRegion": "Selangor",
      "postalCode": "47301",
      "addressCountry": "MY"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "areaServed": "MY",
      "availableLanguage": ["English", "Malay", "Chinese"]
    },
    "sameAs": [
      siteUrl
    ]
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <html lang={language} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={fullKeywords} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Alternate Language URLs */}
      {alternateUrls.map(({ lang, url }) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${siteUrl}/en${canonicalPath.replace(/^\/(en|bm|zh)/, '')}`} />
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={`${siteUrl}${ogImage}`} />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content={language === 'bm' ? 'ms_MY' : language === 'zh' ? 'zh_CN' : 'en_US'} />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${siteUrl}${ogImage}`} />
      
      {/* Robots */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      
      {/* Additional SEO Tags */}
      <meta name="author" content="EWANDS SDN BHD" />
      <meta name="geo.region" content="MY-10" />
      <meta name="geo.placename" content="Petaling Jaya" />
      <meta name="geo.position" content="3.1390;101.6869" />
      <meta name="ICBM" content="3.1390, 101.6869" />
      
      {/* Organization JSON-LD once on homepage only — reduces duplicate snippets on inner URLs */}
      {isHomePage && (
        <script type="application/ld+json">
          {JSON.stringify(defaultStructuredData)}
        </script>
      )}
      
      {/* Page-specific Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
